import React, { useMemo } from "react";
import { SettingsItem } from "@vms/AiResult/AiResultSettings";
import { getSettingsColumns, useGetAiSettingsFilters } from "@vms/SelectFilters/useGetAiSettingsFilters";
import { getTopTrendsSortingColumnName, TOP_TRENDS_CATEGORIES } from "@models/TopTrends";
import { TooltipOptionsList } from "@vms/SelectFilters/aiSettingsFilters.helpers";
import { DEFAULT_GROWTH_PERIOD } from "@constants/general";
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";

export function useAiSearchTopTrendsSettings() {
    const {
        isLoading: isAiSearchSettingsLoading,
        topTrendsSettings,
    } = useAiSearchSettingsContext();

    const getAiSettingsFilters = useGetAiSettingsFilters();

    return useMemo(
        (): SettingsItem[][] => {
            if (isAiSearchSettingsLoading) return [];

            if (!topTrendsSettings) return [];

            const {
                apiFilters,
                drillWords,
                growthPeriod,
                includeLessOnePercentPenetration,
                includeLessTenMenusCount,
                showOnlyNewItems,
                topTrendsCategoriesIds,
                sortingColumn,
            } = topTrendsSettings;

            let filtersSettings: SettingsItem[] = [];

            if (apiFilters) {
                filtersSettings = getAiSettingsFilters(apiFilters);
            }

            const settingsColumns = getSettingsColumns(filtersSettings);

            const selectedCategories = TOP_TRENDS_CATEGORIES.filter(
                c => topTrendsCategoriesIds.includes(c.id)
            );

            let categoryLabel = ': All';

            if (selectedCategories.length === 1) {
                categoryLabel = `: ${selectedCategories[0].name}`;
            } else if (selectedCategories.length > 1) {
                categoryLabel = ` (${selectedCategories.length})`;
            }

            const values: SettingsItem['values'] = [
                {
                    id: "sorting",
                    title: `Sort By: ${getTopTrendsSortingColumnName(sortingColumn)}`,
                },
                {
                    id: "categories",
                    title: `Narrow by Category${categoryLabel}`,
                    tooltip: selectedCategories.length === 1 ? null : (
                        <TooltipOptionsList data={selectedCategories}/>
                    ),
                },
            ];


            if (drillWords && drillWords.length > 0) {
                values.push({
                    id: "drillWords",
                    title: "Drill Words: " + drillWords.map(i => i.name).join(", "),
                });
            }

            if (growthPeriod !== DEFAULT_GROWTH_PERIOD && growthPeriod !== undefined) {
                values.push({
                    id: "growthPeriod",
                    title: `Years growth timeframe: ${growthPeriod}`,
                });
            }

            if (showOnlyNewItems) {
                values.push({
                    id: "showOnlyNewItems",
                    title: "New Items Only",
                });
            }

            if (!includeLessTenMenusCount && includeLessOnePercentPenetration) {
                values.push({
                    id: "includeLessTenMenusCount",
                    title: "More than 10 menus",
                });
            }

            if (includeLessTenMenusCount && includeLessOnePercentPenetration) {
                values.push({
                    id: "includeAllItems",
                    title: "All items",
                });
            }

            settingsColumns[1].unshift({
                id: "general",
                title: "Top Trends Settings",
                values,
            });

            return settingsColumns;
        },
        [
            isAiSearchSettingsLoading,
            getAiSettingsFilters,
            topTrendsSettings,
        ]
    );
}